import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, Dropdown, Grid, Icon, Picture, Text } from "~/components-shared";
import { useDispatch, useSelector } from "react-redux";
import { AppEnum, IntegrationTypeEnum, StatusStateEnum } from "~/types/enums";
import { usePostHog } from "posthog-js/react";
import api from "~/lib/api";
import authActionCreators from "~/store/auth/actions";
import { handleError } from "~/lib/helpers";
import lists from "~/lib/lists";
import styled, { useTheme } from "styled-components";
import companySelectors from "~/store/company/selectors";
import { useTranslation } from "react-i18next";
import companyActionCreators from "~/store/company/actions";
import Markdown from "~/components/Markdown";
const LockIcon = styled(Icon)`
  position: absolute;
  top: 0.8em;
  right: 0.8em;
`;
const PopularIcon = styled(Icon)`
  position: absolute;
  top: 0.8em;
  left: 0.8em;
  z-index: 1;
`;
const freeOptions = [{
  id: 1,
  app: AppEnum.SLACK,
  type: IntegrationTypeEnum.SLACK,
  popular: true,
  info: `💡 <b>Select specific channels as you integration or add @Joyn at any time</b><br><br>
- We will map relevant messages and threads where Joyn is added.<br>
- Remove Joyn from the channel at any time.`
}, {
  id: 2,
  app: AppEnum.JIRA,
  type: IntegrationTypeEnum.JIRA,
  popular: true,
  info: `💡 <b>Select specific projects or connect all.</b><br><br>
We will map relevant:<br>
- Tickets<br>
- Projects<br>
- Comments<br><br>
Based on title, description, assignee, status, and issue type.
`
}, {
  id: 3,
  app: AppEnum.LINEAR,
  type: IntegrationTypeEnum.LINEAR,
  popular: true,
  info: `💡 <b>Select specific projects or connect all.</b><br><br>
We will map relevant:<br>
  - Tickets<br>
  - Projects<br>
  - Comments<br><br>
- Based on title, description, assignee, status, and issue type.`
}, {
  id: 4,
  app: AppEnum.ASANA,
  type: IntegrationTypeEnum.ASANA,
  info: `💡 <b>Select specific projects or connect all.</b><br><br>
We will map relevant:<br>
  - Projects<br>
  - Tasks<br>
  - Milestones<br><br>
- Based on Title, Description, and other related fields.`
}, {
  id: 5,
  app: AppEnum.ZENDESK,
  type: IntegrationTypeEnum.SLACK,
  viaSlack: true,
  info: `💡 <b>Select specific channels with <string>Zendesk<strong> Slack bot as you integration or add @Joyn at any time</b><br><br>
- We will map relevant messages and threads where Joyn is added.<br>
- Remove Joyn from the channel at any time.`
}];
const paidOptions = [{
  id: 1,
  app: AppEnum.SALESFORCE,
  type: IntegrationTypeEnum.SALESFORCE,
  popular: true
}, {
  id: 2,
  app: AppEnum.GITHUB,
  type: IntegrationTypeEnum.GITHUB,
  popular: true
}, {
  id: 3,
  app: AppEnum.NOTION,
  type: IntegrationTypeEnum.NOTION
}, {
  id: 4,
  app: AppEnum.PRODUCTBOARD,
  type: IntegrationTypeEnum.PRODUCTBOARD
}, {
  id: 5,
  app: AppEnum.GONG,
  type: IntegrationTypeEnum.GONG
}, {
  id: 6,
  app: AppEnum.MONDAY,
  type: IntegrationTypeEnum.MONDAY
}, {
  id: 7,
  app: AppEnum.CONFLUENCE,
  type: IntegrationTypeEnum.CONFLUENCE
}];
const SignUpOnboardingSelectIntegrations = _ref => {
  let {
    onContinue,
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const posthog = usePostHog();
  const companyStatus = useSelector(companySelectors.getStatus);
  const company = useSelector(companySelectors.getCompany);
  const [selectedIntegrations, setSelectedIntegrations] = useState(company?.settings.preferredIntegrations || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const dispatch = useDispatch();
  useEffect(() => {
    if (companyStatus === StatusStateEnum.IDLE) {
      dispatch(companyActionCreators.fetchStarted());
    }
  }, [companyStatus, dispatch]);
  useEffect(() => {
    setSelectedIntegrations(company?.settings.preferredIntegrations || []);
  }, [company]);
  const handleSubmit = () => {
    if (selectedIntegrations.length) {
      const data = {
        types: selectedIntegrations
      };
      setLoading(true);
      api.onboardingSelectIntegrations(data).then(_ref2 => {
        let {
          token
        } = _ref2;
        dispatch(authActionCreators.updateTokenStarted(token));
        dispatch(companyActionCreators.fetchStarted());
        posthog.capture(`SignUp Onboarding (Integrations) – Continue`, {
          ...data
        });
        onContinue();
      }).catch(e => setError(handleError(e))).finally(() => {
        setLoading(false);
      });
    }
  };
  const handleIntegrationSelect = app => {
    if (selectedIntegrations.includes(app)) {
      setSelectedIntegrations(selectedIntegrations.filter(item => item !== app));
    } else {
      const freeOption = freeOptions.find(option => option.app === app);
      if (freeOption?.viaSlack && !selectedIntegrations.includes(AppEnum.SLACK)) {
        setSelectedIntegrations(prev => [...prev, app, AppEnum.SLACK]);
      } else {
        setSelectedIntegrations(prev => [...prev, app]);
      }
    }
  };
  const handleMeetJoyn = () => {
    window.open("https://calendly.com/kendra-joyn/30-mins", "_blank");
  };
  return /*#__PURE__*/React.createElement(Card, rest, /*#__PURE__*/React.createElement(Text, {
    fontSize: 5,
    lineHeight: 7,
    align: "center",
    weight: "semiBold",
    s: {
      mb: 5
    },
    style: {
      maxWidth: "800px"
    }
  }, t("SignUpOnboardingSelectIntegrations.selectIntegrations")), /*#__PURE__*/React.createElement(Box, {
    style: {
      maxWidth: "780px"
    },
    align: "center",
    direction: "column"
  }, /*#__PURE__*/React.createElement(Dropdown, {
    display: "inline-block",
    direction: "bottomLeft",
    s: {
      mb: 5
    },
    handlerProps: {
      rounded: true,
      color: "translucentPrimary",
      fontSize: 3,
      s: {
        py: 1,
        px: 3
      }
    },
    handler: "\uD83D\uDCA1 Learn more"
  }, _ref3 => {
    let {
      isOpen
    } = _ref3;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen,
      width: "360px",
      style: {
        transform: "translateX(115px)",
        padding: "1em"
      }
    }, /*#__PURE__*/React.createElement(Markdown, {
      text: t("SignUpOnboardingSelectIntegrations.extraInfo")
    }));
  }), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 8
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Grid, {
    cols: 5,
    gap: 5,
    s: {
      mb: 8
    }
  }, freeOptions.map(option => /*#__PURE__*/React.createElement(Card, {
    key: option.id,
    minHeight: "140px",
    maxWidth: "140px",
    onClick: () => handleIntegrationSelect(option.app),
    fullWidth: true,
    align: "center",
    outline: selectedIntegrations.includes(option.app),
    color: selectedIntegrations.includes(option.app) ? "translucentPrimary" : "background",
    shadow: true
  }, /*#__PURE__*/React.createElement(Picture, {
    width: "48px",
    height: "48px",
    s: {
      mb: 3,
      mt: 3
    },
    src: lists.common.apps.logos(theme)[option.app],
    backgroundSize: "contain"
  }), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    fontSize: 4,
    color: "text"
  }, lists.common.apps.names[option.app]), option.viaSlack && /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    fontSize: 3,
    color: "textSecondary"
  }, "via Slack"), option.popular && /*#__PURE__*/React.createElement(PopularIcon, {
    src: "starFill",
    color: "#d7bb08",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": "Popular"
  }), option.info && /*#__PURE__*/React.createElement(LockIcon, {
    style: {
      zIndex: 1
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": option.info,
    src: "infoOutline",
    color: "textSecondary"
  })))), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1fr auto",
    fullWidth: true,
    justify: "start",
    align: "center",
    s: {
      mb: 5
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    color: "textSecondary",
    weight: "semiBold"
  }, t("SignUpOnboardingSelectIntegrations.paidPlan")), /*#__PURE__*/React.createElement(Button, {
    color: "translucentPrimary",
    s: {
      px: 5,
      py: 1.5
    },
    fontSize: 3.5,
    onClick: handleMeetJoyn
  }, t("SignUpOnboardingSelectIntegrations.chatSales"))), /*#__PURE__*/React.createElement(Grid, {
    cols: 5,
    gap: 5,
    s: {
      mb: 8
    }
  }, paidOptions.map(option => /*#__PURE__*/React.createElement(Card, {
    key: option.id,
    minHeight: "140px",
    maxWidth: "140px",
    style: {
      opacity: 0.7
    },
    fullWidth: true,
    align: "center",
    outline: selectedIntegrations.includes(option.app),
    color: "background",
    shadow: true
  }, /*#__PURE__*/React.createElement(Picture, {
    width: "48px",
    height: "48px",
    s: {
      mb: 3,
      mt: 3
    },
    src: lists.members.types.logos(theme)[option.type],
    backgroundSize: "contain"
  }), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    fontSize: 4,
    color: "text"
  }, lists.integrations.types.names[option.type]), option.popular && /*#__PURE__*/React.createElement(PopularIcon, {
    src: "starFill",
    color: "#d7bb08",
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": "Popular"
  }), /*#__PURE__*/React.createElement(LockIcon, {
    src: "lock",
    color: "textSecondary"
  }))))), /*#__PURE__*/React.createElement(Box, {
    justify: "center"
  }, /*#__PURE__*/React.createElement(Button, {
    loading: loading,
    disabled: !selectedIntegrations.length,
    onClick: handleSubmit
  }, t("common.actions.saveAndContinue"))));
};
export default SignUpOnboardingSelectIntegrations;